// src/lib/useReveal.js
// Reveal-on-scroll for the marketing page. Elements tagged `.reveal` fade and
// slide up once when they first enter the viewport, then stop being observed —
// no scroll listener, no library.
//
// Two safety nets, because `.reveal` starts at opacity 0 and a page that never
// reveals is a blank page:
//   - `prefers-reduced-motion` and browsers without IntersectionObserver get
//     everything revealed immediately.
//   - if no callback has arrived at all after a beat (a throttled background
//     tab, an embedding that doesn't composite), everything is revealed anyway.

import { useEffect } from "react";

const FAILSAFE_MS = 1500;

export default function useReveal(deps = []) {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll(".reveal:not(.reveal--in)"));
    if (!nodes.length) return undefined;

    const revealAll = () => nodes.forEach((n) => n.classList.add("reveal--in"));

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced || typeof IntersectionObserver === "undefined") {
      revealAll();
      return undefined;
    }

    let delivered = false;
    const io = new IntersectionObserver(
      (entries) => {
        delivered = true;
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add("reveal--in");
          io.unobserve(e.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
    );

    nodes.forEach((n) => io.observe(n));

    // The observer reports on the first frame after observing, so anything
    // still silent by now is never coming — show the content rather than
    // leaving it invisible.
    const failsafe = window.setTimeout(() => {
      if (delivered) return;
      io.disconnect();
      revealAll();
    }, FAILSAFE_MS);

    return () => {
      window.clearTimeout(failsafe);
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
